import React, { useState } from 'react';

const ComparisonTable = () => {
  const [activeTab, setActiveTab] = useState('basic'); // 'basic', 'advanced'
  const [activeDevice, setActiveDevice] = useState('enterprise'); // For mobile view: 'basic', 'pro', 'elite', 'enterprise'

  // Device comparison data
  const devices = [
    {
      id: 'basic',
      name: 'AeroNode Basic',
      price: '499',
      features: {
        miningFactor: '1.0x',
        baseAPY: '30%',
        maxAPY: '69%',
        hardware: '1',
        stakingCapacity: '10,000 AFI',
        activation: '1,000 AFI',
        activationTooltip: 'Minimum staking amount required to activate the device',
        networkProtection: 'No',
        registrationFee: '500',
        registrationTooltip: '50% of fee is burned, 50% goes to reward pool',
        temporalAdvantage: '+50%',
        temporalTooltip: 'Early adopters receive up to 50% higher rewards through our Temporal Reward Adjustment mechanism'
      }
    },
    {
      id: 'pro',
      name: 'AeroNode Pro',
      price: '999',
      features: {
        miningFactor: '2.0x',
        baseAPY: '40%',
        maxAPY: '180%',
        hardware: '2',
        stakingCapacity: '25,000 AFI',
        activation: '2,000 AFI',
        activationTooltip: 'Minimum staking amount required to activate the device',
        networkProtection: 'Basic',
        registrationFee: '400',
        registrationTooltip: '50% of fee is burned, 50% goes to reward pool',
        temporalAdvantage: '+50%',
        temporalTooltip: 'Early adopters receive up to 50% higher rewards through our Temporal Reward Adjustment mechanism'
      }
    },
    {
      id: 'elite',
      name: 'AeroNode Elite',
      price: '1,999',
      features: {
        miningFactor: '3.0x',
        baseAPY: '50%',
        maxAPY: '350%',
        hardware: '4',
        stakingCapacity: '60,000 AFI',
        activation: '3,000 AFI',
        activationTooltip: 'Minimum staking amount required to activate the device',
        networkProtection: 'Advanced',
        registrationFee: '300',
        registrationTooltip: '50% of fee is burned, 50% goes to reward pool',
        temporalAdvantage: '+50%',
        temporalTooltip: 'Early adopters receive up to 50% higher rewards through our Temporal Reward Adjustment mechanism'
      }
    },
    {
      id: 'enterprise',
      name: 'AeroNode Enterprise',
      price: '4,999',
      features: {
        miningFactor: '6.0x',
        baseAPY: '60%',
        maxAPY: '600%',
        maxAPYTooltip: 'Expected Annual Yield is token-denominated, not USD value. Actual returns depend on protocol revenue.',
        hardware: '8',
        stakingCapacity: '160,000 AFI',
        activation: '5,000 AFI',
        activationTooltip: 'Minimum staking amount required to activate the device',
        networkProtection: 'Priority',
        networkTooltip: 'Enterprise nodes receive priority when network approaches maximum emission capacity',
        registrationFee: '200',
        registrationTooltip: '50% of fee is burned, 50% goes to reward pool',
        temporalAdvantage: '+50%',
        temporalTooltip: 'Early adopters receive up to 50% higher rewards through our Temporal Reward Adjustment mechanism'
      }
    }
  ];

  // Feature list with proper order and display names
  const featureList = [
    { id: 'price', name: 'Price (USDT)' },
    { id: 'miningFactor', name: 'Mining Factor' },
    { id: 'baseAPY', name: 'Base Expected Annual Yield (no staking)' },
    { id: 'maxAPY', name: 'Max Expected Annual Yield (with staking)' },
    { id: 'hardware', name: 'Hardware Units' },
    { id: 'stakingCapacity', name: 'Staking Capacity' },
    { id: 'activation', name: 'Activation Requirement' },
    { id: 'networkProtection', name: 'Network Saturation Protection' },
    { id: 'registrationFee', name: 'Registration Fee (AFI)' },
    { id: 'temporalAdvantage', name: 'Temporal Advantage' }
  ];

  // Mobile card device switcher
  const DeviceSelector = () => {
    return (
      <div className="device-tabs-mobile">
        <div className="device-tabs-scroll">
          {devices.map(device => (
            <button 
              key={device.id}
              className={`device-tab ${activeDevice === device.id ? 'active' : ''}`}
              onClick={() => setActiveDevice(device.id)}
            >
              {device.name}
            </button>
          ))}
        </div>
        <div className="device-tabs-indicator">
          <div className="swipe-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  };

  // Mobile feature card layout for a specific device
  const FeatureCards = ({ device }) => {
    const isHighlighted = device.id === 'enterprise';
    
    return (
      <div className={`device-features ${activeDevice === device.id ? 'active' : ''}`} data-device={device.id}>
        <div className="device-header">
          <h3>{device.name}</h3>
          <div className="device-price">{device.price} USDT</div>
        </div>
        
        {featureList.map((feature, index) => {
          // Skip the price since we showed it in the header
          if (feature.id === 'price') return null;
          
          const value = device.features[feature.id];
          const tooltipKey = `${feature.id}Tooltip`;
          const hasTooltip = !!device.features[tooltipKey];
          const tooltipText = device.features[tooltipKey];
          
          // Add AFI suffix for features that need it
          const displayValue = feature.id === 'registrationFee' || feature.id === 'activation' 
            ? `${value} AFI` 
            : value;
            
          // Determine if value should be highlighted
          const isValueHighlighted = isHighlighted && 
            ['miningFactor', 'maxAPY', 'hardware', 'stakingCapacity', 'activation', 'networkProtection', 'registrationFee'].includes(feature.id);
            
          return (
            <div className="feature-card" key={feature.id}>
              <div className="feature-name">{feature.name}</div>
              <div className={`feature-value ${isValueHighlighted ? 'highlight' : ''}`}>
                {displayValue}
                {hasTooltip && (
                  <div className="feature-tooltip">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <div className="tooltip-text">{tooltipText}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <section className="comparison" id="comparison">
      <div className="container">
        <h2 className="section-title">Device <span>Comparison</span></h2>
        <p className="comparison-description">
          Compare AeroFi RWA devices side-by-side to find the perfect match for your investment goals and connectivity needs.
        </p>

        <div className="tab-selector">
          <button 
            className={`btn ${activeTab === 'basic' ? '' : 'btn-outline'}`} 
            onClick={() => setActiveTab('basic')}
          >
            Basic Comparison
          </button>
          <button 
            className={`btn ${activeTab === 'advanced' ? '' : 'btn-outline'}`} 
            onClick={() => setActiveTab('advanced')}
          >
            Advanced Details
          </button>
        </div>
        
        {activeTab === 'basic' && (
          <div className="basic-comparison">
            {/* Mobile tabs for device selection */}
            <DeviceSelector />
            
            {/* Mobile view - Feature cards for selected device */}
            <div className="feature-cards">
              {devices.map(device => (
                <FeatureCards key={device.id} device={device} />
              ))}
            </div>
            
            {/* Desktop view - Full comparison table */}
            <div className="responsive-table-wrapper">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Features</th>
                    <th>AeroNode Basic</th>
                    <th>AeroNode Pro</th>
                    <th>AeroNode Elite</th>
                    <th>AeroNode Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Price (USDT)</td>
                    <td>499</td>
                    <td>999</td>
                    <td>1,999</td>
                    <td>4,999</td>
                  </tr>
                  <tr>
                    <td>Mining Factor</td>
                    <td>1.0x</td>
                    <td>2.0x</td>
                    <td>3.0x</td>
                    <td className="highlight">6.0x</td>
                  </tr>
                  <tr>
                    <td>Base Expected Annual Yield (no staking)</td>
                    <td>30%</td>
                    <td>40%</td>
                    <td>50%</td>
                    <td>60%</td>
                  </tr>
                  <tr>
                    <td>Max Expected Annual Yield (with staking)</td>
                    <td>69%</td>
                    <td>180%</td>
                    <td>350%</td>
                    <td className="highlight tooltipped">600%<span className="tooltip">Expected Annual Yield is token-denominated, not USD value. Actual returns depend on protocol revenue.</span></td>
                  </tr>
                  <tr>
                    <td>Hardware Units</td>
                    <td>1</td>
                    <td>2</td>
                    <td>4</td>
                    <td className="highlight">8</td>
                  </tr>
                  <tr>
                    <td>Staking Capacity</td>
                    <td>10,000 AFI</td>
                    <td>25,000 AFI</td>
                    <td>60,000 AFI</td>
                    <td className="highlight">160,000 AFI</td>
                  </tr>
      
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {activeTab === 'advanced' && (
          <div className="advanced-comparison">
            <div className="glass-card">
              <h3>RWA Asset Packages and Device Models</h3>
              
              <h4 className="sub-heading">Core Asset Structure</h4>
              <p>The foundation of the Aledger ecosystem is built on Real World Asset (RWA) packages, each containing a specific number of WiFi devices. These asset packages provide baseline yields, while the devices serve as staking containers to maximize returns.</p>
              
              <h4 className="sub-heading">Asset Package and Device Relationship</h4>
              <div className="responsive-table-wrapper">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Asset Package</th>
                      <th>Price (USDT)</th>
                      <th>Number of Devices</th>
                      <th>Base Expected Annual Yield (No Staking)</th>
                      <th>Staking Capacity per Device</th>
                      <th>Total Staking Capacity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Basic</td>
                      <td className="number-cell">499</td>
                      <td className="number-cell">1</td>
                      <td className="number-cell">30%</td>
                      <td className="number-cell">10,000 AFI</td>
                      <td className="number-cell">10,000 AFI</td>
                    </tr>
                    <tr>
                      <td>Pro</td>
                      <td className="number-cell">999</td>
                      <td className="number-cell">2</td>
                      <td className="number-cell">40%</td>
                      <td className="number-cell">12,500 AFI</td>
                      <td className="number-cell">25,000 AFI</td>
                    </tr>
                    <tr>
                      <td>Elite</td>
                      <td className="number-cell">1,999</td>
                      <td className="number-cell">4</td>
                      <td className="number-cell">50%</td>
                      <td className="number-cell">15,000 AFI</td>
                      <td className="number-cell">60,000 AFI</td>
                    </tr>
                    <tr>
                      <td>Enterprise</td>
                      <td className="number-cell">4,999</td>
                      <td className="number-cell">8</td>
                      <td className="number-cell">60%</td>
                      <td className="number-cell">20,000 AFI</td>
                      <td className="number-cell">160,000 AFI</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <h4 className="sub-heading">Device Distribution and Logistics</h4>
              <ul>
                <li><strong>Ordering Process</strong>: Orders placed through the official website using USDT payment</li>
                <li><strong>Shipping Timeline</strong>: Standard delivery in 7 business days</li>
                <li><strong>Activation Requirements</strong>: Devices must be registered and activated on the <a href="https://app.aeronyx.network/" target="_blank" rel="noopener noreferrer">app.aeronyx.network</a> platform upon receipt</li>
              </ul>
              
              <h4 className="sub-heading">Device Registration Fees</h4>
              <p>Registration fees are automatically deducted from mining rewards according to the following standards:</p>
              
              <div className="responsive-table-wrapper">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Device Type</th>
                      <th>Registration Fee (AFI)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Basic Device</td>
                      <td className="number-cell">500 AFI</td>
                    </tr>
                    <tr>
                      <td>Pro Device</td>
                      <td className="number-cell">400 AFI</td>
                    </tr>
                    <tr>
                      <td>Elite Device</td>
                      <td className="number-cell">300 AFI</td>
                    </tr>
                    <tr>
                      <td>Enterprise Device</td>
                      <td className="number-cell">200 AFI</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p><strong>Registration Fee Distribution</strong>:</p>
              <ul>
                <li>50% allocated to token burning, directly removed from circulation</li>
                <li>50% allocated to the registration fee reward pool, distributed every 30 days to top nodes based on team performance</li>
              </ul>
              
              <h4 className="sub-heading">Operational Requirements</h4>
              <ul>
                <li><strong>Minimum Uptime</strong>: Devices must maintain over 85% uptime to receive full mining rewards</li>
                <li><strong>Maintenance Requirements</strong>: Quarterly system updates required, approximately 2 hours of maintenance time (not counted as offline time)</li>
              </ul>
              
              <div className="highlight-box">
                <p><strong>Important Note</strong>: Device purchase prices are collected in USDT, but all mining rewards are distributed in AFI tokens, calculated on a token-denominated basis</p>
              </div>
              
              <h4 className="sub-heading">Asset Package Returns and Device Staking</h4>
              <p>Aledger implements an innovative dual-layer return model: asset packages provide baseline returns, while devices require staking to activate additional returns.</p>
              
              <h4 className="sub-heading">Asset Package Baseline Returns</h4>
              <p>Asset packages provide baseline returns without requiring staking:</p>
              <ul>
                <li>Basic Package: 30% baseline Expected Annual Yield</li>
                <li>Pro Package: 40% baseline Expected Annual Yield</li>
                <li>Elite Package: 50% baseline Expected Annual Yield</li>
                <li>Enterprise Package: 60% baseline Expected Annual Yield</li>
              </ul>
              
              <p><strong>No-Stake Returns Tied Directly to Protocol Revenue</strong>:</p>
              <ul>
                <li>Baseline Expected Annual Yield represents expected returns when protocol revenue is sufficient</li>
                <li>Actual daily returns adjust dynamically based on the previous day's protocol sales</li>
                <li>If protocol revenue is insufficient, actual returns will be proportionally reduced</li>
                <li>If there is no protocol revenue, no-stake baseline returns will be zero (except for limited subsidies during initial phases)</li>
              </ul>
              
              <p><strong>Baseline Return Distribution Mechanism</strong>:</p>
              <ul>
                <li>Automatically calculated and distributed to user wallets daily</li>
                <li>Users can choose to withdraw directly or auto-reinvest to the staking pool</li>
                <li>Returns distributed in AFI tokens, calculated on a token-denominated basis</li>
              </ul>
              
              <h4 className="sub-heading">Device Activation and Additional Returns</h4>
              <p>Devices can now be activated without staking, but AFI token staking generates additional returns:</p>
              
              <h5>1. Device Activation Staking</h5>
              <p>Each device can be activated with or without AFI staking. For increased returns, the following staking amounts are recommended:</p>
              <ul>
                <li>Basic Device: 1,000 AFI</li>
                <li>Pro Device: 2,000 AFI</li>
                <li>Elite Device: 3,000 AFI</li>
                <li>Enterprise Device: 5,000 AFI</li>
              </ul>
              
              <h5>2. Device Return Calculation</h5>
              <div className="code-formula">
                Device Return = Device Base Value × Staking Ratio Coefficient × Time Multiplier × Network Dynamic Adjustment Factor
              </div>
              
              <h5>3. Staking Ratio Coefficient</h5>
              <ul>
                <li>No staking: 0.3×</li>
                <li>Minimum activation staking: 0.5×</li>
                <li>Staking 25% of capacity: 0.8×</li>
                <li>Staking 50% of capacity: 1.0×</li>
                <li>Staking 75% of capacity: 1.3×</li>
                <li>Staking 100% of capacity: 1.5×</li>
              </ul>
              
              <h5>4. Time Multiplier</h5>
              <p>Additional rewards based on staking lock period:</p>
              
              <div className="responsive-table-wrapper">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Lock Period</th>
                      <th>Reward Multiplier</th>
                      <th>Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>30 days</td>
                      <td className="number-cell">1.0×</td>
                      <td>Short-term</td>
                    </tr>
                    <tr>
                      <td>60 days</td>
                      <td className="number-cell">1.1×</td>
                      <td>Short-term</td>
                    </tr>
                    <tr>
                      <td>90 days</td>
                      <td className="number-cell">1.2×</td>
                      <td>Short-term</td>
                    </tr>
                    <tr>
                      <td>120 days</td>
                      <td className="number-cell">1.3×</td>
                      <td>Medium-term</td>
                    </tr>
                    <tr>
                      <td>150 days</td>
                      <td className="number-cell">1.4×</td>
                      <td>Medium-term</td>
                    </tr>
                    <tr>
                      <td>180 days</td>
                      <td className="number-cell">1.5×</td>
                      <td>Medium-term</td>
                    </tr>
                    <tr>
                      <td>240 days</td>
                      <td className="number-cell">1.6×</td>
                      <td>Long-term</td>
                    </tr>
                    <tr>
                      <td>300 days</td>
                      <td className="number-cell">1.8×</td>
                      <td>Long-term</td>
                    </tr>
                    <tr>
                      <td>365 days</td>
                      <td className="number-cell">2.0×</td>
                      <td>Long-term</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ComparisonTable;
